import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Divider, Selection } from '@heroui/react';
import { Outlet } from 'react-router';
import { ErrorWidget } from '@/widgets/ErrorWidget';
import { CustomBreadcrumbs } from '@/widgets/CustomBreadcrumbs';
import { useGetEnrollmentIntakesQuery } from '@/entities/Enrollment';
import { routerPath } from '@/shared/config/router';
import { getErrorMessage } from '@/shared/lib/utils/getErrorMessage';
import { CustomSelect } from '@/shared/ui/Fields';
import { SectionLoader } from '@/shared/ui/Loader';
import { Section } from '@/shared/ui/Section';

export const EnrollmentIntakeView = () => {
  const { enrollmentId, intakeTermId } = useParams();
  const [selectedTerm, setSelectedTerm] = useState<string>('');
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetEnrollmentIntakesQuery(enrollmentId || '', {
    skip: !enrollmentId,
  });
  const currentTerm = intakeTermId || selectedTerm;

  if (isLoading) {
    return <SectionLoader />;
  }

  // Раньше любая ошибка выглядела как «не найдено» — включая отказ по долгу и падение
  // сервера. Сообщение сервера полезнее выдуманного.
  if (error) {
    return <ErrorWidget error={getErrorMessage(error) ?? 'Failed to load enrollment intakes'} />;
  }

  if (!data) {
    return <ErrorWidget error="Enrollment intakes not found" />;
  }

  const { course, terms, intake } = data;

  const termOptions = terms
    .filter((term) => term.intakeTermId !== null)
    .map((term) => ({
      key: String(term.intakeTermId),
      label: `#${term.number}`,
    }));

  const breadcrumbs = [
    { label: 'Education', href: routerPath.enrollments },
    { label: `${course.title} ${intake.title}` },
  ];

  const handleSelectTerm = (value: Selection) => {
    const selected = Array.from(value)[0];
    if (selected) {
      const termId = String(selected);
      setSelectedTerm(termId);
      navigate(`/enrollments/${enrollmentId}/terms/${termId}`);
    }
  };

  return (
    <section className="flex flex-col gap-y-6">
      <Section className="flex flex-col gap-y-4">
        <CustomBreadcrumbs items={breadcrumbs} />
        <h2 className="text-[32px] font-medium">
          {course.title} {intake.title}
        </h2>
        <Divider as="div" className="h-[2px] bg-neutral-hover" />
        <CustomSelect
          label="Term"
          labelPlacement="outside"
          placeholder="Select a term"
          style="black"
          items={termOptions}
          isRequired={false}
          className="w-[320px]"
          selectedKeys={currentTerm ? new Set([currentTerm]) : new Set()}
          onSelectionChange={handleSelectTerm}
        />
      </Section>
      <Outlet />
    </section>
  );
};
